import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'

// ---------------------------------------------------------------------------
// Types — these mirror the backend contract in `src/api/agents.py`. The backend
// is being built in parallel, so every field is treated as potentially absent:
// the pages render "—"/empty states rather than crashing on missing data.
// ---------------------------------------------------------------------------

/** Row shape returned by `GET /api/agents` and the summary block of the detail
 *  endpoint. `kind`/`purpose` come from the org's DB phone binding; the rest
 *  from the live Retell agent. */
export interface AgentSummary {
  agent_id: string
  agent_name?: string | null
  published_version?: number | null
  voice_id?: string | null
  language?: string | null
  /** inbound | outbound | both — from the phone binding. */
  kind?: string | null
  purpose?: string | null
  /** E.164 numbers bound to this agent, from the org's Retell phone bindings. */
  phone_numbers?: string[] | null
}

/** One entry in an agent's version list. */
export interface AgentVersion {
  version: number
  is_published?: boolean
  version_title?: string | null
  version_description?: string | null
  last_modification_timestamp?: number | null
}

export interface AgentDetail extends AgentSummary {
  versions: AgentVersion[]
}

// --- Full per-version config, shaped into sections by the backend -----------

export interface AgentVersionPrompt {
  general_prompt?: string | null
  begin_message?: string | null
  model?: string | null
  starting_state?: string | null
}

/** State-machine node. Retell shapes vary, so extra keys are tolerated. */
export interface AgentState {
  name?: string | null
  state_prompt?: string | null
  edges?: unknown[]
  tools?: unknown[]
  [key: string]: unknown
}

/** A configured tool (general_tools entry). Extra keys tolerated. */
export interface AgentTool {
  type?: string | null
  name?: string | null
  description?: string | null
  url?: string | null
  [key: string]: unknown
}

/** Voice / audio behaviour block (backend key `voice_audio`). Only the known
 *  keys are typed; the backend may pass through more, so index access is
 *  allowed. */
export interface AgentVoice {
  voice_id?: string | null
  voice_model?: string | null
  voice_speed?: number | null
  voice_temperature?: number | null
  volume?: number | null
  interruption_sensitivity?: number | null
  responsiveness?: number | null
  enable_backchannel?: boolean | null
  backchannel_frequency?: number | null
  denoising_mode?: string | null
  ambient_sound?: string | null
  language?: string | null
  stt_mode?: string | null
  [key: string]: unknown
}

/** One post-call analysis output definition. Extra keys tolerated. */
export interface AgentPostCallField {
  type?: string | null
  name?: string | null
  description?: string | null
  [key: string]: unknown
}

/** The version config sections, nested under `sections` by the backend. */
export interface AgentVersionSections {
  prompt?: AgentVersionPrompt | null
  states?: AgentState[] | null
  tools?: AgentTool[] | null
  /** Backend key is `voice_audio`. */
  voice_audio?: AgentVoice | null
  /** Backend key is `post_call_analysis`. */
  post_call_analysis?: AgentPostCallField[] | null
}

export interface AgentVersionConfig {
  agent_id: string
  version: number
  agent_name?: string | null
  is_published?: boolean
  version_title?: string | null
  version_description?: string | null
  llm_id?: string | null
  sections?: AgentVersionSections | null
}

// --- Evaluation payload -----------------------------------------------------

export interface AgentEvalPassive {
  call_count?: number | null
  /** Fraction 0..1, or null when no call in the bucket carried a verdict. */
  call_successful_rate?: number | null
  /** e.g. { "true": 8, "false": 2, "unknown": 1 } */
  call_successful_counts?: Record<string, number> | null
  /** e.g. { Positive: 12, Neutral: 4, Negative: 1 } */
  sentiment_distribution?: Record<string, number> | null
  /** Rollup of provider custom_analysis keys → { value → count }. */
  custom_analysis?: Record<string, Record<string, number>> | null
}

export interface AgentEvalActive {
  id: string
  org_id?: string | null
  agent_id?: string | null
  version: number
  summary?: string | null
  /** The skill's structured critique, already parsed (possibly null). */
  verdict?: unknown
  call_id?: string | null
  created_by?: string | null
  /** Epoch SECONDS (float). */
  created_at?: number | null
}

export interface AgentVersionEval {
  passive?: AgentEvalPassive | null
  active?: AgentEvalActive[] | null
}

// ---------------------------------------------------------------------------
// Hooks — all org-scoped server-side via the session; no org id in the path.
// ---------------------------------------------------------------------------

export function useAgents() {
  return useQuery({
    queryKey: ['agents'],
    // Backend wraps the list in `{ agents: [...] }`; unwrap so callers keep
    // working with a bare array.
    queryFn: async (): Promise<AgentSummary[]> => {
      const res = await apiFetch<{ agents?: AgentSummary[] | null }>('/api/agents')
      return res.agents ?? []
    },
  })
}

export function useAgent(agentId: string | undefined) {
  return useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => apiFetch<AgentDetail>(`/api/agents/${agentId}`),
    enabled: !!agentId,
  })
}

export function useAgentVersion(agentId: string | undefined, version: number | undefined) {
  return useQuery({
    queryKey: ['agent', agentId, 'version', version],
    queryFn: () => apiFetch<AgentVersionConfig>(`/api/agents/${agentId}/versions/${version}`),
    enabled: !!agentId && version != null,
  })
}

export function useAgentVersionEval(agentId: string | undefined, version: number | undefined) {
  return useQuery({
    queryKey: ['agent', agentId, 'version', version, 'eval'],
    queryFn: () => apiFetch<AgentVersionEval>(`/api/agents/${agentId}/versions/${version}/eval`),
    enabled: !!agentId && version != null,
  })
}
